import {
  Avatar,
  Box,
  Flex,
  Menu,
  MenuButton,
  MenuDivider,
  MenuItem,
  MenuList,
  Text,
} from '@chakra-ui/react';
import { updateShowTermsStatus } from 'actions/home';
import { ROLES } from 'helpers/enum';
import {
  checkAuthenticationAdmin,
  checkAuthenticationSuperAdmin,
  getUserInfoStorage,
} from 'helpers/lib';
import { ChevronDown, KeyRound, LogOut, ScrollText } from 'lucide-react';
import { useTranslation } from 'react-i18next';
import { useDispatch } from 'react-redux';
import { Link as ReactLink } from 'react-router-dom';

import { useLogout } from '../../contexts/LogoutContext';

const ROLE_LABEL_KEY: Record<string, string> = {
  superadmin: 'shell.role.superadmin',
  admin: 'shell.role.admin',
  user: 'shell.role.user',
};

interface UserMenuProps {
  /** opens the "My Company" management modal (admin only) */
  onOpenCompany?: () => void;
  /** when true, render compact (avatar-only) trigger for collapsed sidebar */
  collapsed?: boolean;
  /** fired after any menu action navigates (used to close the mobile drawer) */
  onNavigate?: () => void;
}

const UserMenu = ({ onOpenCompany, collapsed = false, onNavigate }: UserMenuProps) => {
  const { t } = useTranslation();
  const dispatch = useDispatch();
  const { logout } = useLogout();

  const isAdmin =
    checkAuthenticationAdmin() === ROLES.ADMIN ||
    checkAuthenticationSuperAdmin() === ROLES.SUPER_ADMIN;
  const isSuperAdmin = checkAuthenticationSuperAdmin() === ROLES.SUPER_ADMIN;
  const roleKey = isSuperAdmin ? 'superadmin' : isAdmin ? 'admin' : 'user';

  const user = getUserInfoStorage();
  const displayName =
    (user?.tokenParsed?.name as string) ||
    (user?.tokenParsed?.preferred_username as string) ||
    (user?.tokenParsed?.email as string) ||
    'Account';

  return (
    <Menu autoSelect={false} matchWidth={!collapsed} placement='top-start'>
      <MenuButton
        as={Flex}
        align='center'
        justifyContent={collapsed ? 'center' : 'flex-start'}
        gap={3}
        w='100%'
        px={collapsed ? 0 : 2}
        py={2}
        borderRadius='md'
        _hover={{ bg: 'primary.soft' }}
        cursor='pointer'
        transition='background 0.14s ease-out'
        aria-label={displayName}
      >
        <Flex align='center' justify={collapsed ? 'center' : 'flex-start'} gap={3} w='100%'>
          <Avatar
            flexShrink={0}
            color='primary.on'
            bg='primary.solid'
            name={displayName}
            size='sm'
          />
          {!collapsed && (
            <>
              <Box flex='1' minW={0} textAlign='left'>
                <Text color='text.default' fontSize='sm' fontWeight={600} noOfLines={1}>
                  {displayName}
                </Text>
                <Text color='text.muted' fontSize='xs' noOfLines={1}>
                  {t(ROLE_LABEL_KEY[roleKey])}
                </Text>
              </Box>
              <Box as={ChevronDown} flexShrink={0} boxSize='16px' color='text.subtle' />
            </>
          )}
        </Flex>
      </MenuButton>
      <MenuList
        zIndex='dropdown'
        minW='220px'
        py={2}
        bg='bg.surfaceRaised'
        borderColor='border.default'
        borderRadius='lg'
        shadow='ds_lg'
      >
        {/* "My company" moved to the sidebar "Usage & members" nav item (Administration). */}
        <MenuItem
          as={ReactLink}
          fontSize='md'
          bg='transparent'
          _hover={{ bg: 'primary.soft' }}
          icon={<KeyRound size={16} />}
          onClick={onNavigate}
          to='/change-password'
        >
          {t('route.changePassword')}
        </MenuItem>
        <MenuItem
          fontSize='md'
          bg='transparent'
          _hover={{ bg: 'primary.soft' }}
          icon={<ScrollText size={16} />}
          onClick={() => {
            dispatch(updateShowTermsStatus(true));
            onNavigate?.();
          }}
        >
          {t('route.termOfService')}
        </MenuItem>
        <MenuDivider borderColor='border.default' />
        <MenuItem
          color='state.error'
          fontSize='md'
          bg='transparent'
          _hover={{ bg: 'state.errorSoft' }}
          icon={<LogOut size={16} />}
          onClick={() => {
            onNavigate?.();
            logout();
          }}
        >
          {t('route.logOut')}
        </MenuItem>
      </MenuList>
    </Menu>
  );
};

export default UserMenu;
